import { startTransition, useEffect, useState } from 'react';
import { Mode } from 'src/@types/mode';
import { useUserRole } from 'src/hooks/useUserRole';
import useApplyOrgSwitch, { useNavigateOrgHome } from 'src/hooks/useApplyOrgSwitch';

/**
 * Applies another tab's org switch or sign-in to this tab.
 *
 * A tab holds its selected org in React state and its Auth0 token in memory,
 * so nothing otherwise tells it that a sibling tab moved. localStorage is the
 * one piece of that state every tab shares, and its storage event fires in
 * every tab but the writer, so `lastOrgId` and `lastUserId` are the signal.
 *
 * This has to be mounted inside the router and below SettingsProvider: an org
 * switch moves the route and the palette as well as the org, and neither is
 * reachable from OrgContextProvider, where the org state itself lives.
 *
 * Returns whether a switch is still waiting for its route. The caller has to
 * hold the org-scoped tree back while it is set, because the swap moves the org
 * before the navigation lands.
 */
export default function useSessionTabSync() {
  const applyOrgSwitch = useApplyOrgSwitch();
  const navigateOrgHome = useNavigateOrgHome();
  const { data: role, isPending: roleLoading } = useUserRole();

  // The route depends on the new org's role, and clearing the cache has just
  // made that a refetch, so the swap lands before the route does. State rather
  // than a ref: the flag itself has to re-run the effect that follows, which no
  // other dependency is guaranteed to do, and the caller reads it to hold the
  // org-scoped tree back meanwhile.
  const [awaitingHomeRoute, setAwaitingHomeRoute] = useState(false);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      // Safari fires the event for a write that sets the value it already had.
      if (!event.key || event.newValue === event.oldValue) return;

      // A different user means a different token, and Auth0 caches tokens in
      // memory, so only a fresh load re-runs its session check.
      if (event.key === 'lastUserId') {
        window.location.reload();
        return;
      }

      if (event.key !== 'lastOrgId') return;

      setAwaitingHomeRoute(true);
      applyOrgSwitch(event.newValue ?? '', (localStorage.getItem('lastMode') as Mode) || 'numinar');
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [applyOrgSwitch]);

  useEffect(() => {
    if (!awaitingHomeRoute) return;

    // The role comes from the org list for most users, and clearing the cache
    // has just made that a refetch. Once it settles, an undefined role means
    // the user holds none in this org, and the route map answers /home, so
    // waiting any longer would strand the tab.
    if (roleLoading) return;

    // One transition for both, so they land in one commit. Releasing the gate
    // is an urgent update while the router wraps its own location update in a
    // transition, and split across two commits the gate opens first: the tree
    // mounts at the route the tab was already on, under the new org, and the
    // location arrives after. Where the router's navigation is urgent instead,
    // the location moves first and the gate is still closed, which is equally
    // safe.
    startTransition(() => {
      setAwaitingHomeRoute(false);
      navigateOrgHome(role);
    });
  }, [awaitingHomeRoute, role, roleLoading, navigateOrgHome]);

  return awaitingHomeRoute;
}
